import { CircleAIAppShell } from "@circle-react-uikit/CircleAIAppShell";
import { PageHeaderContainer } from "@circle-react/components/Layout/WorkspacePage/components/Header/HeaderContainer";
import { useLocation } from "@tanstack/react-router";
import { type ReactNode, useEffect } from "react";

import { CanvasInset } from "@/react/components/Layout/CircleAIAppShellLayout/CanvasInset";
import {
  useIsPageChromeHidden,
  useIsPromoLockedToPlansPage,
} from "@/react/components/Layout/CircleAIAppShellLayout/ShellBypassContext";
import { useLayoutContext } from "@/react/components/SettingsApp/Layout/LayoutContext";
import { PAGE_SURFACE } from "@/react/constants/pageSurface";

import { SETTINGS_WORKSPACE_HEADER_ID } from "./constants";

interface SettingsShellDesktopProps {
  children: ReactNode;
  shouldHideSidebarNestedNavBar?: boolean;
}

export const SettingsShellDesktop = ({
  children,
  shouldHideSidebarNestedNavBar = false,
}: SettingsShellDesktopProps) => {
  const { setConfig } = useLayoutContext();
  const isPageChromeHidden = useIsPageChromeHidden();
  const isPromoLockedToPlansPage = useIsPromoLockedToPlansPage();
  const pathname = useLocation({ select: location => location.pathname });
  const shouldHideSettingsShellChrome =
    isPageChromeHidden || pathname === "/settings" || pathname === "/settings/";

  useEffect(() => {
    setConfig(prev => ({ ...prev, shouldHideSidebarNestedNavBar }));
  }, [shouldHideSidebarNestedNavBar, setConfig]);

  return (
    // Drop the canvas header only for a `lock_to_plans_page` promo. Pages such
    // as the theme builder hide their surrounding chrome but still render
    // actions like Save into this header.
    <CanvasInset alwaysShowHeader={!isPromoLockedToPlansPage}>
      <CircleAIAppShell.Layout.Workspace>
        <CircleAIAppShell.Layout.Workspace.Header className={PAGE_SURFACE}>
          {!shouldHideSettingsShellChrome && <PageHeaderContainer />}
          <div
            id={SETTINGS_WORKSPACE_HEADER_ID}
            className={shouldHideSettingsShellChrome ? "hidden" : undefined}
          />
        </CircleAIAppShell.Layout.Workspace.Header>
        <CircleAIAppShell.Layout.Workspace.Body className={PAGE_SURFACE}>
          <div className="relative flex min-h-0 w-full flex-1 flex-col">
            {children}
          </div>
        </CircleAIAppShell.Layout.Workspace.Body>
      </CircleAIAppShell.Layout.Workspace>
    </CanvasInset>
  );
};
